import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Turns each add-on row into an add/remove toggle.
 *
 * Switch state is always derived from the cart itself, never from the DOM. The
 * theme's morph preserves this element and only patches the `checked` attribute,
 * but once a checkbox has been clicked its checkedness is "dirty" and the HTML
 * spec stops the attribute from reflecting into the property. Reading the cart
 * sidesteps that entirely.
 *
 * Requests are serialised rather than rejected while one is in flight. Dropping
 * the second click made a quick off-then-on look broken: the cart kept the first
 * result and only a page refresh made the switch agree with reality again.
 */

const SECTION_ID = 'cart-drawer-section';
const SOURCE = 'cart-addons';

class CartAddons extends HTMLElement {
  /** Serialises cart writes so no click is lost. */
  #chain = Promise.resolve();

  /** Number of queued or running writes, used only to drive `aria-busy`. */
  #pending = 0;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  /**
   * Re-syncs every switch after any cart change, including ones made elsewhere
   * (a product page, the reward reconciler, another tab's app).
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(() => this.#refresh())
      .catch(() => {
        /* A failed cart update leaves the switches as they were. */
      });
  };

  /** Sets each switch from the live cart contents. */
  async #refresh() {
    const toggles = [...this.querySelectorAll('[data-addon-toggle]')].filter(
      (node) => node instanceof HTMLInputElement
    );

    if (!toggles.length) return;

    try {
      const cart = await this.#fetchCart();
      const variantsInCart = new Set(cart.items.map((item) => item.variant_id));

      for (const toggle of toggles) {
        toggle.checked = variantsInCart.has(Number(toggle.dataset.variantId));
      }
    } catch (error) {
      console.warn('[cart-addons] Could not sync switches:', error);
    }
  }

  async #fetchCart() {
    const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Could not read cart: ${response.status}`);

    return response.json();
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const toggle = event.target;
    if (!(toggle instanceof HTMLInputElement) || !toggle.hasAttribute('data-addon-toggle')) return;

    const variantId = Number(toggle.dataset.variantId);
    if (!variantId) return;

    this.#apply(variantId, toggle.checked);
  };

  /**
   * Queues one cart write behind whatever is already running. The desired state
   * is read from the click that produced it, so a rapid off-then-on ends with
   * the add-on in the cart, matching what the customer last asked for.
   *
   * @param {number} variantId
   * @param {boolean} shouldBeInCart
   */
  #apply(variantId, shouldBeInCart) {
    this.#pending += 1;
    this.setAttribute('aria-busy', 'true');

    this.#chain = this.#chain
      .then(() => (shouldBeInCart ? this.#add(variantId) : this.#remove(variantId)))
      .catch((error) => {
        console.warn('[cart-addons] Could not update add-on:', error);
      })
      .then(() => this.#refresh())
      .finally(() => {
        this.#pending -= 1;
        if (this.#pending === 0) this.removeAttribute('aria-busy');
      });

    return this.#chain;
  }

  /** @param {number} variantId */
  async #add(variantId) {
    // Adding what is already there would create a second line at quantity 2.
    const current = await this.#fetchCart();
    if (current.items.some((item) => item.variant_id === variantId)) return;

    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: SECTION_ID,
      sections_url: window.location.pathname,
    });

    const url = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
    const response = await fetch(url, fetchConfig('json', { body }));
    const payload = await response.json();

    if (payload.status && payload.status !== 200) {
      throw new Error(payload.description || payload.message || 'Could not add add-on');
    }

    // add.js answers with the added lines, not the cart: no item_count, no
    // currency. The header badge reads `cart.totalQuantity ?? detail.itemCount ?? 0`
    // and would blank itself. Re-read the cart, keep the rendered sections.
    const cart = await this.#fetchCart();

    this.#announce({ ...cart, sections: payload.sections }, 'add');
  }

  /** @param {number} variantId */
  async #remove(variantId) {
    const cart = await this.#fetchCart();
    const line = cart.items.find((item) => item.variant_id === variantId);

    // Already gone — nothing to do.
    if (!line) return;

    const body = JSON.stringify({
      id: line.key,
      quantity: 0,
      sections: SECTION_ID,
      sections_url: window.location.pathname,
    });

    const url = window.Theme?.routes?.cart_change_url ?? '/cart/change.js';
    const response = await fetch(url, fetchConfig('json', { body }));
    const payload = await response.json();

    if (payload.errors) throw new Error(payload.errors);

    // change.js already answers with a full cart.
    this.#announce(payload, 'remove');
  }

  /**
   * @param {Record<string, any>} payload - A full AJAX cart, plus `sections`.
   * @param {'add' | 'update' | 'remove'} action
   */
  #announce(payload, action) {
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );

    let cart;

    try {
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(payload);
    } catch {
      // Analytics shape is optional; itemCount below still drives the badge.
    }

    deferred.resolve({
      cart,
      detail: {
        sections: payload.sections,
        items: payload.items,
        itemCount: payload.item_count,
        source: SOURCE,
        didError: false,
      },
    });
  }
}

if (!customElements.get('cart-addons')) {
  customElements.define('cart-addons', CartAddons);
}
